import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/** Count-up length, in ms. Kept short so the final numbers are readable almost immediately. */
const COUNT_UP_DURATION = 1200;

/**
 * Stats strip. Counts numeric values up from zero once, the first time the strip scrolls into view.
 * Values are rendered in full by Liquid, so the strip reads correctly without JavaScript, and the
 * final frame restores the exact rendered text (for example "35,291" or "1M+").
 *
 * @extends {Component}
 */
class StatsStrip extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {number[]} */
  #frames = [];

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#teardown();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  #setup() {
    // Badges are short promises rather than numbers, so they never count up.
    if (prefersReducedMotion() || this.hasAttribute('data-counted')) return;
    if (this.classList.contains('stats-strip__list--badges')) return;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        this.#observer?.disconnect();
        this.setAttribute('data-counted', '');
        this.#countUp();
      },
      { threshold: 0.4 }
    );
    this.#observer.observe(this);
  }

  #teardown() {
    this.#observer?.disconnect();
    this.#observer = undefined;
    for (const frame of this.#frames) cancelAnimationFrame(frame);
    this.#frames = [];
  }

  #countUp() {
    for (const element of this.querySelectorAll('[data-count-up]')) {
      const text = element.textContent?.trim() ?? '';
      const parsed = parseCountable(text);
      if (!parsed) continue;

      // Hold the final width so neighbouring stats don't shift while digits change.
      if (element instanceof HTMLElement) element.style.minWidth = `${element.offsetWidth}px`;

      const start = performance.now();
      const index = this.#frames.length;

      const tick = (/** @type {number} */ now) => {
        const progress = Math.min((now - start) / COUNT_UP_DURATION, 1);
        const eased = 1 - Math.pow(1 - progress, 3);

        element.textContent =
          progress < 1 ? parsed.prefix + parsed.format(parsed.value * eased) + parsed.suffix : text;

        if (progress < 1) this.#frames[index] = requestAnimationFrame(tick);
      };

      this.#frames[index] = requestAnimationFrame(tick);
    }
  }
}

/**
 * Splits text like "35,291", "4.8" or "1M+" into prefix, number and suffix.
 * Returns null when there is no number to count.
 * @param {string} text
 */
function parseCountable(text) {
  const match = text.match(/^(\D*?)(\d[\d,]*(?:\.\d+)?)(.*)$/);
  if (!match) return null;

  const [, prefix = '', numberText = '', suffix = ''] = match;
  const value = Number(numberText.replaceAll(',', ''));
  const decimals = numberText.split('.')[1]?.length ?? 0;

  // Counting up to a single digit (the 1 in "1M+") reads as a glitch rather than a flourish.
  if (!Number.isFinite(value) || (decimals === 0 && value < 10)) return null;

  const grouped = numberText.includes(',');

  return {
    prefix,
    suffix,
    value,
    /** @param {number} current */
    format(current) {
      const fixed = current.toFixed(decimals);
      if (!grouped) return fixed;

      const [whole = '', fraction] = fixed.split('.');
      const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return fraction ? `${withCommas}.${fraction}` : withCommas;
    },
  };
}

if (!customElements.get('stats-strip')) {
  customElements.define('stats-strip', StatsStrip);
}
